import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock,
  CheckCircle2,
  AlertTriangle,
  Users,
  Play,
  Pause,
  Plus,
  Edit,
  Trash2,
  X,
  RefreshCw
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { adminService, PerformanceCycle, CycleCreate } from '@/services/api/adminService';
import { useReviewCycleConfig } from '@/hooks/useReviewCycleConfig';
import toast from 'react-hot-toast';
import CyclePopulationDialog from './CyclePopulationDialog';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

// Review type options for create form
const REVIEW_TYPE_OPTIONS = [
  { value: 'midyear', label: 'Mid-Year Check-in', cycleType: 'formal' as const, defaultStart: '-01-01', defaultEnd: '-06-30' },
  { value: 'yearend', label: 'Year-End Check-in', cycleType: 'formal' as const, defaultStart: '-07-01', defaultEnd: '-12-31' },
  { value: 'q1_checkin', label: 'Q1 Check-in', cycleType: 'informal' as const, defaultStart: '-03-01', defaultEnd: '-03-31' },
  { value: 'q3_checkin', label: 'Q3 Check-in', cycleType: 'informal' as const, defaultStart: '-09-01', defaultEnd: '-09-30' },
  // The Annual I&G Review window. Its dates are the go-live control:
  // One-Ups can schedule once the cycle exists; People Leaders can prepare only
  // while it is Active and today is inside the dates. No rating, no calibration.
  { value: 'talent_review', label: 'Annual I&G Review', cycleType: 'informal' as const, defaultStart: '-09-11', defaultEnd: '-12-31' },
];

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused', label: 'Paused' },
];

const PerformanceCycleAdmin: React.FC = () => {
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // The cycle whose "who's counted" list is open (HR UAT list #20-#22).
  const [populationCycleId, setPopulationCycleId] = useState<number | null>(null);
  const [editingCycle, setEditingCycle] = useState<PerformanceCycle | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    review_type: 'midyear',
    year: currentYear,
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    calibration_end_date: '',
    launch_message: '',
    status: 'planning',
    requires_rating: true,
    requires_calibration: true,
    requires_anonymous_feedback: false,
  });

  // The Mid-Year → Year-End cutover is DERIVED from the current year's Year-End
  // cycle's start_date (edit that cycle below to move it); reviewCycleCfg exposes the
  // resolved date for the read-only summary. queryClient refreshes it when cycles change.
  const queryClient = useQueryClient();
  const reviewCycleCfg = useReviewCycleConfig();

  useEffect(() => {
    fetchCycles();
  }, []);

  const fetchCycles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getPerformanceCycles();
      setCycles(data.cycles);
    } catch (err) {
      console.error('Failed to fetch impact & growth cycles:', err);
      setError('Failed to load impact & growth cycles');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      review_type: 'midyear',
      year: currentYear,
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      calibration_end_date: '',
      launch_message: '',
      status: 'planning',
      requires_rating: true,
      requires_calibration: true,
      requires_anonymous_feedback: false,
    });
  };

  const handleReviewTypeChange = (reviewType: string) => {
    const option = REVIEW_TYPE_OPTIONS.find(o => o.value === reviewType);
    if (!option) return;

    const year = formData.year;
    const name = `${option.label} ${year}`;
    const isFormal = option.cycleType === 'formal';

    setFormData(prev => ({
      ...prev,
      review_type: reviewType,
      name,
      start_date: `${year}${option.defaultStart}`,
      end_date: `${year}${option.defaultEnd}`,
      requires_rating: isFormal,
      requires_calibration: isFormal,
      requires_anonymous_feedback: false,
    }));
  };

  const handleYearChange = (year: number) => {
    const option = REVIEW_TYPE_OPTIONS.find(o => o.value === formData.review_type);
    const label = option?.label || formData.review_type;

    setFormData(prev => ({
      ...prev,
      year,
      name: `${label} ${year}`,
      start_date: option ? `${year}${option.defaultStart}` : prev.start_date,
      end_date: option ? `${year}${option.defaultEnd}` : prev.end_date,
    }));
  };

  const openCreateForm = () => {
    resetForm();
    handleReviewTypeChange('midyear');
    setEditingCycle(null);
    setShowCreateForm(true);
  };

  const openEditForm = (cycle: PerformanceCycle) => {
    setEditingCycle(cycle);
    setFormData({
      review_type: cycle.review_type,
      year: cycle.year,
      name: cycle.name,
      description: cycle.description || '',
      start_date: cycle.start_date,
      end_date: cycle.end_date,
      calibration_end_date: cycle.calibration_end_date || '',
      launch_message: cycle.launch_message || '',
      status: cycle.status,
      requires_rating: cycle.requires_rating,
      requires_calibration: cycle.requires_calibration,
      requires_anonymous_feedback: cycle.requires_anonymous_feedback,
    });
    setShowCreateForm(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const option = REVIEW_TYPE_OPTIONS.find(o => o.value === formData.review_type);

      if (editingCycle) {
        await adminService.updateCycle(editingCycle.id, {
          name: formData.name,
          description: formData.description || undefined,
          start_date: formData.start_date,
          end_date: formData.end_date,
          calibration_end_date: formData.calibration_end_date || null,
          launch_message: formData.launch_message || null,
          status: formData.status,
          requires_rating: formData.requires_rating,
          requires_calibration: formData.requires_calibration,
          requires_anonymous_feedback: formData.requires_anonymous_feedback,
        });
        toast.success('Cycle updated successfully');
      } else {
        const createData: CycleCreate = {
          name: formData.name,
          description: formData.description || undefined,
          review_type: formData.review_type,
          cycle_type: option?.cycleType || 'formal',
          year: formData.year,
          start_date: formData.start_date,
          end_date: formData.end_date,
          calibration_end_date: formData.calibration_end_date || null,
          launch_message: formData.launch_message || null,
          status: formData.status,
          requires_rating: formData.requires_rating,
          requires_calibration: formData.requires_calibration,
          requires_anonymous_feedback: formData.requires_anonymous_feedback,
        };
        await adminService.createCycle(createData);
        toast.success('Cycle created successfully');
      }

      setShowCreateForm(false);
      setEditingCycle(null);
      fetchCycles();
      // The Mid-Year → Year-End boundary is derived from the Year-End cycle's
      // start_date, so refresh it app-wide whenever a cycle changes.
      queryClient.invalidateQueries({ queryKey: ['review-cycle-config'] });
    } catch (err: any) {
      console.error('Error saving cycle:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save cycle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cycle: PerformanceCycle) => {
    if (!confirm(`Are you sure you want to delete "${cycle.name}"?`)) return;
    try {
      await adminService.deleteCycle(cycle.id);
      toast.success(`"${cycle.name}" deleted`);
      fetchCycles();
      queryClient.invalidateQueries({ queryKey: ['review-cycle-config'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to delete cycle');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Play className="w-4 h-4 text-emerald-600" />;
      case 'planning':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-gray-600" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-700';
      case 'planning':
        return 'bg-blue-100 text-blue-700';
      case 'completed':
        return 'bg-gray-100 text-gray-700';
      case 'paused':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Impact & growth Cycle Administration</h2>
          <p className="text-gray-600 mt-1">
            Monitor and manage Impact & growth check-in cycles across the organisation
          </p>
        </div>
        <Button className="bg-heritage-blue hover:bg-heritage-blue/90" onClick={openCreateForm}>
          <Plus className="w-4 h-4 mr-2" />
          Create New Cycle
        </Button>
      </div>

      {/* Mid-Year → Year-End cutover — DERIVED from the Year-End cycle's start date */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-heritage-blue/10 rounded-lg shrink-0">
            <Calendar className="w-6 h-6 text-heritage-blue" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Mid-Year → Year-End cutover</h3>
            <p className="text-sm text-gray-600 mt-1">
              The switch is driven by the <span className="font-medium">Year-End</span> cycle's start
              date. Currently{' '}
              <span className="font-medium">
                {MONTH_OPTIONS.find(m => m.value === reviewCycleCfg.h2_start_month)?.label} {reviewCycleCfg.h2_start_day}
              </span>{' '}
              — Mid-Year runs Jan 1 up to the day before; Year-End runs from this date to Dec 31.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              To move the cutover, edit the current year's Year-End cycle's start date below.
            </p>
          </div>
        </div>
      </Card>

      {/* Create / Edit Form */}
      {showCreateForm && (
        <Card className="p-6 border-heritage-blue/30 bg-blue-50/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingCycle ? `Edit: ${editingCycle.name}` : 'Create New Check-in Cycle'}
            </h3>
            <button onClick={() => { setShowCreateForm(false); setEditingCycle(null); }} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Review Type */}
            {!editingCycle && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Type</label>
                <select
                  value={formData.review_type}
                  onChange={e => handleReviewTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
                >
                  {REVIEW_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Year */}
            {!editingCycle && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={formData.year}
                  onChange={e => handleYearChange(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
                >
                  {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
                placeholder="e.g. Mid-Year Check-in 2025"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
              />
              {formData.review_type === 'yearend' && (
                <p className="text-xs text-gray-500 mt-1">
                  This is the Mid-Year → Year-End cutover; the Mid-Year cycle's end date follows it.
                </p>
              )}
            </div>

            {/* End Date — auto-managed for Mid-Year (must end the day before Year-End begins) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={e => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                disabled={formData.review_type === 'midyear'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50 disabled:bg-gray-100 disabled:text-gray-500"
              />
              {formData.review_type === 'midyear' && (
                <p className="text-xs text-gray-500 mt-1">
                  Auto-set to the day before the Year-End cycle starts (the cutover).
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">Check-ins close on this date. Too Soon To Rate counts back from it.</p>
            </div>

            {/* Calibration end date and launch alert (HR UAT list #23) */}
            {(formData.review_type === 'midyear' || formData.review_type === 'yearend') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calibration End Date</label>
                  <input
                    type="date"
                    value={formData.calibration_end_date}
                    onChange={e => setFormData(prev => ({ ...prev, calibration_end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to close calibration with the check-ins.</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Launch message</label>
                  <textarea
                    value={formData.launch_message}
                    onChange={e => setFormData(prev => ({ ...prev, launch_message: e.target.value }))}
                    rows={3}
                    maxLength={1000}
                    placeholder="Shown in the app to everyone in this cycle once it is active. Leave empty for a standard message with both end dates."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
                  />
                </div>
              </>
            )}

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Configuration toggles */}
          <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_rating}
                onChange={e => setFormData(prev => ({ ...prev, requires_rating: e.target.checked }))}
                className="rounded border-gray-300 text-heritage-blue focus:ring-heritage-blue"
              />
              Requires Impact & Growth Indicator
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_calibration}
                onChange={e => setFormData(prev => ({ ...prev, requires_calibration: e.target.checked }))}
                className="rounded border-gray-300 text-heritage-blue focus:ring-heritage-blue"
              />
              Requires Calibration
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_anonymous_feedback}
                onChange={e => setFormData(prev => ({ ...prev, requires_anonymous_feedback: e.target.checked }))}
                className="rounded border-gray-300 text-heritage-blue focus:ring-heritage-blue"
              />
              Requires Anonymous Feedback
            </label>
          </div>

          {/* Description */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue/50 resize-none"
              placeholder="Instructions or notes for this cycle..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateForm(false); setEditingCycle(null); }}>
              Cancel
            </Button>
            <Button className="bg-heritage-blue hover:bg-heritage-blue/90" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingCycle ? 'Update Cycle' : 'Create Cycle'}
            </Button>
          </div>
        </Card>
      )}

      {/* Cycle List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Impact & growth Check-in Cycles</h3>
          <Button variant="outline" size="sm" onClick={fetchCycles} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <Card className="p-12">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-8 h-8 animate-spin text-heritage-blue" />
            </div>
          </Card>
        ) : error ? (
          <Card className="p-6">
            <div className="text-center text-red-600">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p>{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchCycles}>
                Try Again
              </Button>
            </div>
          </Card>
        ) : cycles.length === 0 ? (
          <Card className="p-8">
            <div className="text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No Check-in Cycles Yet</p>
              <p className="text-sm mt-2 max-w-md mx-auto">
                Create your first organisational check-in cycle to define when 1:1 Check-ins
                take place. Standard cycles are Mid-Year (Jan–Jun) and Year-End (Jul–Dec).
              </p>
              <Button className="mt-4 bg-heritage-blue hover:bg-heritage-blue/90" onClick={openCreateForm}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Cycle
              </Button>
            </div>
          </Card>
        ) : (
          cycles.map((cycle) => (
            <Card key={cycle.id} className="p-6">
              <div className="space-y-4">
                {/* Cycle Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-heritage-blue/10 rounded-lg">
                      <Calendar className="w-6 h-6 text-heritage-blue" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h4 className="text-lg font-semibold text-gray-900">{cycle.name}</h4>
                        <span className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 ${getStatusColor(cycle.status)}`}>
                          {getStatusIcon(cycle.status)}
                          {cycle.status.toUpperCase()}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          cycle.type === 'formal' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {cycle.type.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {new Date(cycle.start_date + 'T00:00:00').toLocaleDateString()} – {new Date(cycle.end_date + 'T00:00:00').toLocaleDateString()}
                      </p>
                      {cycle.calibration_end_date && cycle.calibration_end_date !== cycle.end_date && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Calibration closes {new Date(cycle.calibration_end_date + 'T00:00:00').toLocaleDateString()}
                        </p>
                      )}
                      {cycle.launched_at && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Launched {new Date(cycle.launched_at).toLocaleDateString()}: everyone in the cycle sees the launch alert
                        </p>
                      )}
                      {cycle.description && (
                        <p className="text-xs text-gray-500 mt-1">{cycle.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(cycle.review_type === 'midyear' || cycle.review_type === 'yearend') && (
                      <Button variant="outline" size="sm" onClick={() => setPopulationCycleId(cycle.id)}>
                        <Users className="w-4 h-4 mr-1" />
                        Who&apos;s counted
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openEditForm(cycle)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(cycle)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Progress Section */}
                {cycle.total_participants > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Completion Progress</span>
                      <span className="font-semibold text-gray-900">
                        {cycle.completed_reviews} / {cycle.total_participants} check-ins
                      </span>
                    </div>
                    <Progress value={cycle.completion_rate} className="h-2" />
                    <p className="text-xs text-gray-500 text-right">{cycle.completion_rate}% complete</p>
                  </div>
                )}

                {/* Configuration badges */}
                <div className="flex gap-2 flex-wrap">
                  {cycle.requires_rating && (
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">Impact & Growth Indicators required</span>
                  )}
                  {cycle.requires_calibration && (
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">Calibration required</span>
                  )}
                  {cycle.requires_anonymous_feedback && (
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">Anonymous feedback required</span>
                  )}
                  {cycle.total_participants === 0 && (
                    <span className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-600">No check-ins scheduled yet</span>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
      <CyclePopulationDialog cycleId={populationCycleId} onClose={() => setPopulationCycleId(null)} />
    </div>
  );
};

export default PerformanceCycleAdmin;
